import * as _ from 'underscore';

import { IGlob } from '../../ts/Glob';
import * as layoutCalc from './layoutCalc';
import { ISizeObj, ISizesObj, IWidgetLayout, IWidgetLayoutElement, OrientationType } from './Interfaces';

export type ElementType = 'column' | 'row';

export const MIN_SIZE = 28;
export const WIDGET_HIDING_SIZE = 1;

interface IAxisObj {
    arr: any[];
    hover: any[];
    edge: boolean;
    waste: boolean;
}

interface ICluster {
    pos: number;
    _pos: number;
    size: number;
    arr: any[];
}

interface IWasteCluster {
    pos: number;
    size: number;
}

type DimType = 0 | 1;

function getReorderDim(pos: number[], _pos: number[]): DimType {
    if (_pos == null) {
        return null;
    }

    const dx = pos[0] - _pos[0];
    const dy = pos[1] - _pos[1];

    if (dx === 0 && dy !== 0) {
        return 0;
    }

    if (dx !== 0 && dy === 0) {
        return 1;
    }

    return null;
}

function excludeWaste(layout: IWidgetLayout, dim: DimType, axis: IAxisObj[], waste: any[], processed) {
    let next = [];

    _.each(waste, (i) => {
        processed[i] = true;

        const pos = layout.pos[i];
        const size = layout.size[i];
        const start = pos[1 - dim];
        const end = pos[1 - dim] + size[1 - dim] - 1;

        _.each(_.range(start, end + 1), (_i) => {
            const obj = axis[_i];

            obj.waste = true;

            next = next.concat(obj.hover);
            next = next.concat(obj.arr);
        });
    });

    next = _.filter(_.unique(next), (i) => {
        return processed[i] !== true;
    });

    if (next.length === 0) {
        return axis;
    }

    return excludeWaste(layout, dim, axis, next, processed);
}

function getAxis(layout: IWidgetLayout, stripe: any[], waste: any[], dim: DimType, length: number) {
    const range = _.range(length);
    const axis = range.map<IAxisObj>(() => {
        return { arr: [], hover: [], edge: true, waste: false };
    });

    _.each(stripe, (i) => {
        const pos = layout.pos[i];
        const size = layout.size[i];
        const start = pos[1 - dim];
        const end = pos[1 - dim] + size[1 - dim] - 1;

        axis[start].arr.push(i);

        _.each(_.range(start, end), (_i) => {
            const obj = axis[_i + 1];

            obj.edge = false;
            obj.hover.push(i);
        });
    });

    return excludeWaste(layout, dim, axis, waste, {});
}

function getWidgetsStripe(layout: IWidgetLayout, dim: DimType, start: number, end: number, index: number) {
    const stripe = [];
    const waste = [];

    let _start: number;
    let _end: number;
    let pos: number[];
    let size: number[];

    let i = layout.pos.length || layout.size.length;

    while (i > 0) {
        i -= 1;

        if (i === index) {
            continue;
        }

        pos = layout.pos[i];
        size = layout.size[i];

        if (pos == null || size == null) {
            continue;
        }

        _start = pos[dim];
        _end = pos[dim] + size[dim] - 1;

        if (_start >= start && _end <= end) {
            stripe.push(i);
        } else if (_start <= end && _end >= start) {
            waste.push(i);
        }
    }

    return {
        stripe: stripe,
        waste: waste
    };
}

function getClusters(axis: IAxisObj[]) {
    const clusters: ICluster[] = [];

    let cluster: ICluster;

    _.each(axis, (_obj, i) => {
        if (_obj.waste || (_obj.edge && _obj.arr.length === 0)) {
            return;
        }

        if (_obj.edge) {
            cluster = {
                pos: i,
                _pos: i,
                size: 1,
                arr: _obj.arr.slice(0)
            };

            clusters.push(cluster);
        } else {
            cluster.size += 1;
            cluster.arr = cluster.arr.concat(_obj.arr);
        }
    });

    return clusters;
}

function getWasteClusters(axis: IAxisObj[]) {
    const clusters: IWasteCluster[] = [];
    const length = axis.length;

    let i = 0;
    let obj: IWasteCluster;

    while (i < length) {
        while (i < length && axis[i].waste !== true) {
            i += 1;
        }

        if (i < length) {
            obj = {
                pos: i,
                size: 1
            };

            clusters.push(obj);
            i += 1;
        }

        while (i < length && axis[i].waste === true) {
            obj.size += 1;
            i += 1;
        }
    }

    return clusters;
}

function getWasteTransforms(wasteClusters: IWasteCluster[], length: number) {
    const trans = new Array(length + 1);

    let cur = 0;
    let shift = 0;
    let _length = length;

    _.each(wasteClusters, (obj) => {
        _.each(_.range(cur, obj.pos), (i) => {
            trans[i] = shift;
        });

        _.each(_.range(obj.pos, obj.pos + obj.size), (i) => {
            trans[i] = null;
        });

        cur = obj.pos + obj.size;
        shift -= obj.size;

        _length -= obj.size;
    });

    _.each(_.range(cur, length + 1), (i) => {
        trans[i] = shift;
    });

    return {
        dir: trans,
        rev: _.map(
            _.filter(trans, (d) => {
                return d !== null;
            }),
            (d) => {
                return -d;
            }
        ),
        length: _length
    };
}

function transformPos(pos: number, size: number, trans) {
    const shift = trans[pos];
    const gap = _.find(_.range(pos, pos + size), (i) => {
        return trans[i] !== shift;
    });

    if (shift == null || gap != null) {
        return null;
    }

    return pos + shift;
}

function getReorderEdge(pos: number, _pos: number, size: number) {
    if (pos - _pos > 0) {
        return pos + size;
    } else {
        return pos;
    }
}

function insertCluster(clusters: ICluster[], pos: number, _pos: number, size: number, index: number, trans) {
    pos = transformPos(pos, size, trans.dir);
    _pos = transformPos(_pos, size, trans.dir);

    if (pos == null || _pos == null) {
        return null;
    }

    const edge = getReorderEdge(pos, _pos, size);

    _.each(clusters, (obj) => {
        obj.pos = obj._pos = transformPos(obj.pos, obj.size, trans.dir);
    });

    const after = _.find(clusters, (obj) => {
        var start = obj.pos;
        var end = obj.pos + obj.size - 1;

        return start >= edge || (end >= edge && end - edge + 1 >= edge - start);
    });

    let insertIndex: number;

    if (after == null) {
        insertIndex = clusters.length;
    } else {
        insertIndex = _.indexOf(clusters, after);
    }

    clusters.splice(insertIndex, 0, {
        pos: pos,
        _pos: pos,
        size: size,
        arr: [index]
    });

    return insertIndex;
}

function adjustCoords(clusters: ICluster[], insertIndex: number, left: number, right: number) {
    let cur = clusters[insertIndex].pos;

    _.reduceRight<ICluster[], number>(
        clusters.slice(0, insertIndex),
        (memo, obj) => {
            const shift = cur - (obj.pos + obj.size - 1);

            if (shift <= 0) {
                obj._pos += shift - 1;
            }

            cur = obj._pos;

            return memo;
        },
        0
    );

    cur = clusters[insertIndex].pos + clusters[insertIndex].size - 1;

    _.reduce<ICluster[], number>(
        clusters.slice(insertIndex + 1),
        (memo, obj) => {
            const shift = obj.pos - cur;

            if (shift <= 0) {
                obj._pos -= shift - 1;
            }

            cur = obj._pos + obj.size - 1;

            return memo;
        },
        0
    );

    _.reduce<ICluster[], number>(
        clusters,
        (left, obj, i) => {
            const shift = obj._pos - left;

            if (shift < 0) {
                obj._pos -= shift;
            }

            return obj._pos + obj.size;
        },
        left
    );

    _.reduceRight<ICluster[], number>(
        clusters,
        (right, obj) => {
            const shift = right - (obj._pos + obj.size - 1);

            if (shift < 0) {
                obj._pos += shift;
            }

            return obj._pos - 1;
        },
        right
    );
}

function adjustWidgetCoords(layout: IWidgetLayout, clusters: ICluster[], dim: DimType, trans) {
    let flag = true;

    _.each(clusters, (obj) => {
        obj.pos = transformPos(obj.pos, obj.size, trans.rev);
        obj._pos = transformPos(obj._pos, obj.size, trans.rev);
        flag = flag && obj.pos !== null && obj._pos !== null;
    });

    if (!flag) {
        return false;
    }

    _.each(clusters, (obj) => {
        const d = obj._pos - obj.pos;

        _.each(obj.arr, (i) => {
            const pos = layout.pos[i];

            pos[1 - dim] += d;
        });
    });

    return true;
}

function processReorder(
    layout: IWidgetLayout,
    isOverlapped: boolean,
    pos: number[],
    size: number[],
    _pos: number[],
    i: number
) {
    if (!isOverlapped) {
        return false;
    }

    const dim = getReorderDim(pos, _pos);

    if (dim == null) {
        return false;
    }

    const stripeRes = getWidgetsStripe(layout, dim, pos[dim], pos[dim] + size[dim] - 1, i);

    if (stripeRes == null) {
        return false;
    }

    const stripe = stripeRes.stripe;
    const waste = stripeRes.waste;
    const length = dim === 0 ? layout.columns.length : layout.rows.length;
    const axis = getAxis(layout, stripe, waste, dim, length);
    const clusters = getClusters(axis);
    const wasteClusters = getWasteClusters(axis);
    const trans = getWasteTransforms(wasteClusters, length);
    const insertIndex = insertCluster(clusters, pos[1 - dim], _pos[1 - dim], size[1 - dim], i, trans);

    if (insertIndex == null) {
        return false;
    }

    adjustCoords(clusters, insertIndex, 0, trans.length - 1);

    layout.pos[i] = pos;

    const adjustRes = adjustWidgetCoords(layout, clusters, dim, trans);

    if (!adjustRes) {
        layout.pos[i] = _pos;

        return false;
    }

    return true;
}

function checkRangesOverlap(r1: number[], r2: number[]) {
    let temp: number[];

    if (r1[0] > r2[0]) {
        temp = r1;
        r1 = r2;
        r2 = temp;
    }

    return r2[0] <= r1[1] && r1[0] <= r2[1];
}

function checkRectanglesOverlap(pos1: number[], size1: number[], pos2: number[], size2: number[]) {
    return (
        checkRangesOverlap([pos1[0], pos1[0] + size1[0] - 1], [pos2[0], pos2[0] + size2[0] - 1]) &&
        checkRangesOverlap([pos1[1], pos1[1] + size1[1] - 1], [pos2[1], pos2[1] + size2[1] - 1])
    );
}

function checkOverlap(layout: IWidgetLayout, pos: number[], size: number[], index: number) {
    const l = layout.pos.length || layout.size.length;

    const overlap = _.find(_.range(l), (_i, i) => {
        if (i === index) {
            return;
        }

        const _pos = layout.pos[i];
        const _size = layout.size[i];

        if (_pos === null || _size === null) {
            return;
        }

        return checkRectanglesOverlap(_pos, _size, pos, size);
    });

    return overlap != null;
}

export function getFirstEmptyCell(layout: IWidgetLayout) {
    const nrows = layout.rows.length;
    const ncols = layout.columns.length;

    for (let i = 0; i < nrows; i++) {
        for (let j = 0; j < ncols; j++) {
            const res = _.find(layout.pos, (pos) => {
                return pos[0] === i && pos[1] === j;
            });

            if (res == null) {
                if (!checkOverlap(layout, [i, j], [1, 1], -1)) {
                    return { x: i, y: j };
                }
            }
        }
    }

    return null;
}

export function getNumberOfEmptyCells(layout: IWidgetLayout) {
    const nrows = layout.rows.length;
    const ncols = layout.columns.length;

    let nEmptyCells = 0;

    for (let i = 0; i < nrows; i++) {
        for (let j = 0; j < ncols; j++) {
            const res = _.find(layout.pos, (pos) => {
                return pos[0] === i && pos[1] === j;
            });

            if (res == null) {
                if (!checkOverlap(layout, [i, j], [1, 1], -1)) {
                    nEmptyCells++;
                }
            }
        }
    }

    return nEmptyCells;
}

export function elementAttach(
    layout: IWidgetLayout,
    i: number,
    x: number,
    y: number,
    remainSize: boolean,
    fresh: boolean,
    removeAction?: () => void
) {
    const pos = [x, y];
    const _pos = layout.pos[i];
    const size = !remainSize ? [1, 1] : layout.size[i];
    const isOverlapped = checkOverlap(layout, pos, size, i);
    const canAdjustReorder = processReorder(layout, isOverlapped, pos, size, _pos, i);

    if (isOverlapped && !canAdjustReorder && fresh === true && typeof removeAction === 'function') {
        removeAction();
    }

    if (isOverlapped && !canAdjustReorder) {
        return;
    }

    layout.pos[i] = pos;
    layout.size[i] = size;

    return layout;
}

function getGridRange(sizesArr: ISizeObj[], start: number, end: number) {
    if (start > end) {
        return;
    }

    let margin = 0;
    let i = 0;
    let startPos: number;
    let endPos: number;

    while (i < sizesArr.length && (startPos == null || endPos == null)) {
        if (i > 0) {
            margin += sizesArr[i - 1].size / 2;
        }

        margin += sizesArr[i].size / 2;

        if (startPos == null && start < margin) {
            startPos = i;
        }

        if (endPos == null && end < margin) {
            endPos = i;
        }

        i += 1;
    }

    if (startPos == null) {
        startPos = sizesArr.length;
    }

    if (endPos == null) {
        endPos = sizesArr.length;
    }

    return {
        start: startPos,
        end: endPos
    };
}

export function workspaceLayoutWidgetResize(
    index: number,
    layout: IWidgetLayout,
    layoutArrCols: ISizeObj[],
    layoutArrRows: ISizeObj[],
    orientation: OrientationType,
    dx: number,
    dy: number
) {
    const pos = layout.pos[index];
    const size = layout.size[index];

    if (pos == null || size == null) {
        return layout;
    }

    const render = layoutCalc.render(pos, size, layoutArrCols, layoutArrRows);

    switch (orientation) {
        case 'top':
            render.top += dy;
            break;

        case 'right':
            render.right += dx;
            break;

        case 'bottom':
            render.bottom += dy;
            break;

        case 'left':
            render.left += dx;
            break;
    }

    const rangeX = getGridRange(layoutArrRows, render.top, render.bottom);
    const rangeY = getGridRange(layoutArrCols, render.left, render.right);

    if (rangeX == null || rangeY == null) {
        return layout;
    }

    const rangePos = [rangeX.start, rangeY.start];
    const rangeSize = [rangeX.end - rangeX.start, rangeY.end - rangeY.start];

    if (rangeSize[0] <= 0 || rangeSize[1] <= 0) {
        return layout;
    }

    const isOverlapped = checkOverlap(layout, rangePos, rangeSize, index);

    if (isOverlapped) {
        return layout;
    }

    layout.pos[index] = rangePos;
    layout.size[index] = rangeSize;
    return layout;
}

function resizeShiftReduceOperation(shift: number, obj: ISizeObj, ingoreMinFix: boolean): number {
    const initial = (obj._size = obj.size);

    if (obj.origin.size != null && !ingoreMinFix) {
        return shift;
    }

    let newSize = obj._size - shift;
    let minSize = MIN_SIZE;

    if (obj.origin.min != null && !ingoreMinFix) {
        minSize = obj.origin.min;
    }

    // Stored initial size may be less than MIN_SIZE in the layouts created before MIN_SIZE has been introduced.
    if (newSize < minSize && initial > minSize) {
        shift = shift - (initial - minSize);
        newSize = minSize;
    } else {
        shift = 0;
    }

    obj._size = newSize;
    return shift;
}

//sizeProp = '_size'
export function setLayoutFromSizes(sizesArr: ISizeObj[], _size: boolean) {
    const ratio: ISizeObj[] = [];

    let totalRatioSize = 0;

    _.each(sizesArr, (obj) => {
        const size = _size ? obj._size : obj.size;

        if (obj.origin.size != null && obj.origin.size !== size) {
            obj.origin.size = size;

            if (size !== WIDGET_HIDING_SIZE) {
                delete obj.origin.hide;
            }

            delete obj.origin.docked;
        }

        if (obj.origin.min != null && obj.origin.min > size) {
            obj.origin.min = size;
        }

        totalRatioSize += size;
        ratio.push(obj);
    });

    _.each(ratio, (obj) => {
        const size = _size ? obj._size : obj.size;

        obj.origin.ratio = size / totalRatioSize;
    });
}

export function workspaceLayoutResize(index: number, sizesObj: ISizesObj, value: number, ingoreMinFix: boolean) {
    const left = sizesObj.arr.slice(0, index + 1);
    const right = sizesObj.arr.slice(index + 1);

    let res = _.reduce(
        right,
        (shift, obj, i) => {
            return resizeShiftReduceOperation(shift, obj, ingoreMinFix);
        },
        value
    );

    if (res > 0) {
        return;
    }

    res = _.reduceRight(
        left,
        (shift, obj, i) => {
            return resizeShiftReduceOperation(shift, obj, ingoreMinFix);
        },
        -value
    );

    if (res > 0) {
        return;
    }

    setLayoutFromSizes(sizesObj.arr, true);
    return sizesObj;
}

export function toggleDockedState(obj: ISizeObj) {
    if (obj.origin.docked) {
        delete obj.origin.docked;

        if (obj.origin.oldSize != null) {
            obj.origin.size = obj.size = obj.origin.oldSize;
            delete obj.origin.oldSize;
        } else {
            if (obj.origin.fixed) {
                obj.origin.size = obj.size;
            } else {
                delete obj.origin.size;
            }
        }
    } else {
        obj.origin.docked = true;

        if (obj.origin.fixed) {
            obj.origin.oldSize = obj.origin.size;
        } else {
            obj.origin.oldSize = obj.size;
        }

        obj.size = obj.origin.size = MIN_SIZE;
    }
}

function toggleDockedLayoutElementState(element: IWidgetLayoutElement) {
    if (element.docked) {
        delete element.docked;

        if (element.oldSize != null) {
            element.size = element.oldSize;
            delete element.oldSize;
        }
    } else {
        element.docked = true;
        element.oldSize = element.size;
        element.size = MIN_SIZE;
    }
}

function normalizeElements(elements: IWidgetLayoutElement[]) {
    if (Array.isArray(elements)) {
        elements.forEach((element) => {
            if (element.docked && !element.allowDocking) {
                toggleDockedLayoutElementState(element);
            }
        });
    }
}

export function normalizeLayout(layout: IWidgetLayout) {
    const result = $.extend(true, {}, layout);

    normalizeElements(result.columns);
    normalizeElements(result.rows);
    return result;
}

function modifySizeWidgetsLayoutAdd(layout: IWidgetLayout, index: number, dimension: number) {
    const l = layout.pos.length || layout.size.length;

    _.each(_.range(l), (_i, i) => {
        const size = layout.size[i];
        const pos = layout.pos[i];

        if (size == null || pos == null) {
            return;
        }

        if (index > pos[dimension] && index < pos[dimension] + size[dimension]) {
            size[dimension] += 1;
        }

        if (index <= pos[dimension]) {
            pos[dimension] += 1;
        }
    });
}

function modifySizeWidgetsLayoutDelete(layout: IWidgetLayout, index: number, dimension: number) {
    const l = layout.pos.length || layout.size.length;
    const find = _.find(_.range(l), (_i, i) => {
        const size = layout.size[i];
        const pos = layout.pos[i];

        if (size == null || pos == null) {
            return false;
        }

        if (index === pos[dimension] && size[dimension] === 1) {
            return true;
        }

        return false;
    });

    if (find != null) {
        return false;
    }

    _.each(_.range(l), (_i, i) => {
        const size = layout.size[i];
        const pos = layout.pos[i];

        if (size == null || pos == null) {
            return;
        }

        if (index >= pos[dimension] && index < pos[dimension] + size[dimension]) {
            if (size[dimension] === 1) {
                layout.size[i] = null;
                layout.pos[i] = null;
            } else {
                size[dimension] -= 1;
            }
        }

        if (index < pos[dimension]) {
            pos[dimension] -= 1;
        }
    });

    return true;
}

export function workspaceLayoutAddAt(
    layout: IWidgetLayout,
    type: ElementType,
    index: number,
    layoutElements: IWidgetLayoutElement[]
) {
    let minRatio: number;

    layoutElements.forEach((element) => {
        if (minRatio == null || element.ratio < minRatio) {
            minRatio = element.ratio;
        }
    });

    layoutElements.splice(index, 0, {
        ratio: minRatio
    });

    switch (type) {
        case 'row':
            modifySizeWidgetsLayoutAdd(layout, index, 0);
            break;

        case 'column':
            modifySizeWidgetsLayoutAdd(layout, index, 1);
            break;
    }
}

export function workspaceLayoutDelete(
    layout: IWidgetLayout,
    type: ElementType,
    index: number,
    layoutElements: IWidgetLayoutElement[],
    glob: IGlob
) {
    let res: boolean;

    switch (type) {
        case 'row':
            res = modifySizeWidgetsLayoutDelete(layout, index, 0);
            break;

        case 'column':
            res = modifySizeWidgetsLayoutDelete(layout, index, 1);
            break;
    }

    if (res === false) {
        glob.AppActions.showNotification_Fail(
            'There is a widget, which will be removed with this ' + type + ". Action can't be completed."
        );
    } else {
        layoutElements.splice(index, 1);
    }
}
